import filter from 'lodash/filter'
import map from 'lodash/map'
import merge from 'lodash/merge'

import type { VdToastNotificationService } from '../../vend-ui/types'
import { DatabaseService } from '../../factories/database/database'
import { isProductActive } from '../../factories/search/product-index-util'
import { PriceManager } from '../../managers/price-manager'
import { RetailerSettingsManager } from '../../managers/retailer-settings-manager'
import { SyncManager } from '../../managers/sync-manager'
import { $http, $log, $q, $rootScope } from '../../ng-compat/imports'
import { WebRegisterStatusManager } from '../../managers/web-register-status-manager'
import { TrainingModeManager } from '../../managers/training-mode-manager'
import { UserManager } from '../../managers/user-manager'
import { RegisterManager } from '../../managers/register-manager'
import { PERMISSIONS } from '../../constants'

const PRODUCT_SUGGESTION_TEMPLATE = `
  <vd-product-badge product="$vdSuggestion.product"
    price="$vdSuggestion.price"
    exact-match="$vdSuggestion.exactMatch"
    variant-count="$vdSuggestion.variantCount"
    highlight="$vdQuery"
    class="vd-suggestion-badge"
    size="small">
  </vd-product-badge>
`

/**
 * Service for accessing the product VdSuggestionSources and VdSuggestionFormatter. Searches via both /api/2.0/suggest
 * (typey) and by exact SKU match directly from the products IndexedDB store.
 *
 * @class ProductSuggestions
 * @module productSuggestions
 */
export class ProductSuggestions {
  private _customerCodeSource: $TSFixMe
  private _formatter: $TSFixMe
  private _globalSource: $TSFixMe
  private _productCodeIndex: $TSFixMe
  private _source: $TSFixMe
  public productErrorTemplate: $TSFixMe

  constructor(
    VdSuggestionSource: $TSFixMe,
    VdSuggestionFormatter: $TSFixMe,
    private vdToastNotificationService: VdToastNotificationService,
    Database: DatabaseService,
    syncManager: SyncManager,
    private priceManager: PriceManager,
    private productsStore: $TSFixMe,
    customerSuggestions: $TSFixMe,
    private userSwitchingService: $TSFixMe,
    private productEmbeddedBarcodeService: $TSFixMe,
    private retailerSettingsManager: RetailerSettingsManager,
    private productModals: $TSFixMe,
    private webRegisterStatusManager: WebRegisterStatusManager,
    private trainingModeManager: TrainingModeManager,
    private userManager: UserManager,
    private registerManager: RegisterManager
  ) {
    'ngInject'
    const types = ['product', 'variant']
    const entityStore = 'products'
    const productEntityDb = Database.store(entityStore)

    const SUGGEST_URL =
      '/api/2.0/suggest?type=product&type=variant&page_size=30'
    const productSearchSource = new VdSuggestionSource({
      types,
      search: ({ query }: $TSFixMe) => {
        const params = { q: query }
        return $http
          .get<{ data: $TSFixMe[] }>(SUGGEST_URL, { params })
          .then(({ data }) => data.data || [])
      },
    })

    this._productCodeIndex = productEntityDb.index('byProductCode')

    // Replaces the default error message in the autocomplete
    this.productErrorTemplate = 'product/product-error.html'

    const productCodeSource = new VdSuggestionSource({
      types,
      init: () => syncManager.whenCompleteEntityDataAvailable(entityStore),
      search: ({ query }: $TSFixMe) => this._searchByProductCode({ query }),
    })

    this._formatter = new VdSuggestionFormatter({
      type: 'product',
      displayKey: 'displayName',
      template: PRODUCT_SUGGESTION_TEMPLATE,
      enhance: (suggestion: $TSFixMe) => this.enhance(suggestion),
    })

    const variantFormatter = new VdSuggestionFormatter({
      type: 'variant',
      displayKey: 'displayName',
      template: PRODUCT_SUGGESTION_TEMPLATE,
      enhance: (suggestion: $TSFixMe) => this.enhance(suggestion),
    })

    this._source = new VdSuggestionSource({
      sources: [productCodeSource, productSearchSource],
      formatters: [this._formatter, variantFormatter],
      feelingLucky: (options: $TSFixMe) => this._feelingLucky(options),
    })

    this._customerCodeSource = customerSuggestions.getSource({
      customerCodeOnly: true,
    })

    const noOpCustomerCodeSource = new VdSuggestionSource({
      types: this._customerCodeSource.types,
      search: () => [],
      init: () => this._customerCodeSource.init(),
    })

    const userSwitchIdFormatter = new VdSuggestionFormatter({
      type: 'user',
      displayKey: 'displayName',
    })

    const canCreateNewProduct = () =>
      this.webRegisterStatusManager.isOnline() &&
      !this.trainingModeManager.trainingModeEnabled &&
      this.userManager.hasPermission(PERMISSIONS.PRODUCT_ADD_EDIT) &&
      this.registerManager.isActiveRegisterOpen()

    this._globalSource = new VdSuggestionSource({
      sources: [productCodeSource, productSearchSource, noOpCustomerCodeSource],
      formatters: [
        this._formatter,
        variantFormatter,
        customerSuggestions.getFormatter(),
        userSwitchIdFormatter,
      ],
      create: ({ query }: $TSFixMe) =>
        this.productModals.quickAddProductBySearchTerm(query),
      canShowCreate: canCreateNewProduct,
      feelingLucky: (options: $TSFixMe) => this._globalFeelingLucky(options),
      allowFeelingLuckyCreate: false,
    })
  }

  /**
   * Get the product VdSuggestionSource for searching for products.
   *
   * @method getSource
   *
   * @param  {Object} [options]
   *
   *         @param {Boolean} [options.global]
   *                If true will return a source for use in the global search. In addition to the standard product
   *                search functionality this global search source will also support barcode scanning customers by
   *                exact customer code match.
   *
   * @return {VdSuggestionSource}
   */
  public getSource(
    options: {
      global?: boolean
    } = {}
  ) {
    if (options.global) {
      return this._globalSource
    }

    return this._source
  }

  /**
   * Get the product VdSuggestionFormatter for enhancing and rendering product suggestions.
   *
   * @method getFormatter
   *
   * @return {VdSuggestionFormatter}
   */
  public getFormatter() {
    return this._formatter
  }

  // ============================== Private Methods ======================== //

  /**
   * Search the products IndexedDB store for products that have the given Product Code.
   *
   * @private
   * @method _searchByProductCode
   *
   *
   * @param  {Object} options
   *         @param {String} options.query The SKU that was searched for
   *
   * @return {Promise<Array<Suggestion>>} A promise that resolves to an array of suggestions for the products that have
   *         the given SKU
   */
  private _searchByProductCode({
    query,
    barcodeData,
  }: {
    query: string
    barcodeData?: $TSFixMe
  }) {
    return this._productCodeIndex
      .getAll(query)
      .then((products: $TSFixMe) => filter(products, isProductActive))
      .then((products: $TSFixMe) => {
        return map(products, product => {
          // We want to give the suggestion the same type that typey would give
          // it so that results will be properly de-duped.
          // We want to always treat variant parents as child variants (since
          // a specific SKU was entered) and regular non-variant products as
          // products, since that's how typey returns them.
          // This all stems from our weird variant model, where a variant
          // parent is, itself, a variant child.
          const type =
            product.has_variants || product.variant_parent_id
              ? 'variant'
              : 'product'
          return {
            id: product.id,
            type,
            [type]: product,
            product,
            exactMatch: true,
            barcodeData,
          }
        })
      })
  }

  /**
   * Enhance a product suggestion - hydrating the product (if it doesn't already exist), a `price` property with the
   * product's base price, a `displayName` property with the appropriate name of the product and the `variantCount` if
   * the product is a variant parent.
   *
   * @method enhance
   *
   * @param  {Suggestion} suggestion
   *
   * @return {Promise<Suggestion} A promise for the enhanced suggestion
   */
  public enhance(suggestion: $TSFixMe) {
    const updatedSuggestion = {}
    return $q((resolve, reject) => {
      $q.all({
        product: suggestion.product || this.productsStore.get(suggestion.id),
        price: this.priceManager.getBasePriceForProduct(suggestion.id),
      })
        .then(({ product, price }) => {
          // Although deleted products arent synced, they are temporarily stored whilst handling sales that contain
          // them. Ensure these don't appear in results.
          if (!product || product.deleted_at) {
            return reject(
              `Product with id ${suggestion.id} could not be found.`
            )
          }

          const exactMatch =
            suggestion.exactMatch || suggestion.type === 'variant'
          const displayName = exactMatch ? product.variant_name : product.name

          merge(updatedSuggestion, suggestion, {
            [suggestion.type]: product,
            product,
            price,
            displayName,
            exactMatch,
          })

          const hasVariants = Boolean(product.variant_options.length)
          return hasVariants
            ? this.productsStore.getVariantCount(suggestion.id)
            : 0
        })
        .then(variantCount => {
          ;(updatedSuggestion as $TSFixMe).variantCount = variantCount
          resolve(updatedSuggestion)
        })
    })
  }

  /**
   * Find a product by exact SKU match. Displays an error notification and rejects if no products or multiple products
   * were found with that SKU.
   *
   * @private
   * @method _feelingLucky
   *
   * @param  {Object} options
   *
   *         @param {String} options.query
   *                The SKU that was searched for
   *
   *         @param {Suggestion} options.pendingSuggestion
   *                The active suggestion in the list that would have been selected if feelingLucky had not been
   *                triggered
   *
   * @return {Promise<Suggestion>} A promise that resolves to the suggestion for the found product, or rejects if
   *         none/multiple were found
   */
  private _feelingLucky({
    query,
    pendingSuggestion,
  }: {
    query: string
    pendingSuggestion: $TSFixMe
  }) {
    return this._searchByProductCode({ query }).then(
      (suggestions: $TSFixMe) => {
        return this._handleFeelingLuckySuggestions({
          query,
          pendingSuggestion,
          suggestions,
        })
      }
    )
  }

  /**
   * Find a product by exact SKU match or customer by exact customer code match. Displays an error notification and
   * rejects if no products, customers or multiple products and customers were found with that SKU/code.
   *
   * @private
   * @method _globalFeelingLucky
   *
   * @param  {Object} options
   *
   *         @param {String} options.query
   *                The SKU/code that was searched for
   *
   *         @param {Suggestion} options.pendingSuggestion
   *                The active suggestion in the list that would have been selected if feelingLucky had not been
   *                triggered
   *
   * @return {Promise<Suggestion>} A promise that resolves to the suggestion for the found product or customer, or
   *         rejects if none/multiple were found
   */
  private _globalFeelingLucky({
    query,
    pendingSuggestion,
  }: {
    query: string
    pendingSuggestion: $TSFixMe
  }) {
    const barcodeOption = this.retailerSettingsManager.getBarcodeOption()
    if (this.userSwitchingService.isUserSwitchId(query)) {
      return this._handleUserSwitchIdQuery({ query })
    } else if (
      barcodeOption &&
      barcodeOption !== 'none' &&
      this.productEmbeddedBarcodeService.isEmbeddedBarcode(query)
    ) {
      // This retailer has embedded barcode option enabled AND we have an embedded barcode. Get the bits
      const barcodeComponents = this.productEmbeddedBarcodeService.extractComponents(
        query
      )
      const searchQuery = barcodeComponents
        ? String(barcodeComponents.SKU)
        : query
      const barcodeData = barcodeComponents
        ? String(barcodeComponents.PriceOrWeight)
        : undefined
      return $q
        .all({
          embeddedProducts: this._searchByProductCode({
            query: searchQuery,
            barcodeData,
          }),
        })
        .then(({ embeddedProducts }) => {
          return this._handleFeelingLuckySuggestions({
            query,
            pendingSuggestion,
            suggestions: embeddedProducts,
            isGlobal: true,
          })
        })
    } else {
      return $q
        .all({
          products: this._searchByProductCode({ query }),
          customers: this._customerCodeSource
            .search({ query })
            .then((data: $TSFixMe) => data.suggestions),
        })
        .then(({ products, customers }) => {
          return this._handleFeelingLuckySuggestions({
            query,
            pendingSuggestion,
            suggestions: products.concat(customers),
            isGlobal: true,
          })
        })
    }
  }

  /**
   * Handles a User Switching Id's (USI's) being entered and passed to a feeling lucky search.
   *
   * @private
   * @method _handleUserSwitchIdQuery
   *
   * @param {String} query
   *        The scanned USI.
   *
   * @return {Suggestion<User>|undefined} The user that was scanned.
   */
  private _handleUserSwitchIdQuery({ query }: { query: string }) {
    return this.userSwitchingService
      .fastSwitch({ switchId: query })
      .then((user: $TSFixMe) => {
        return {
          user,
          displayName: user.username,
          type: 'user',
        }
      })
  }

  /**
   * Process the suggestions returned by a feeling lucky search. If there is exactly one suggestion then that
   * suggestion will be returned.If there are 0 or multiple suggestions it will either return the pendingSuggestion or
   * display a toast error notification.
   *
   * @private
   * @method _handleFeelingLuckySuggestions
   *
   * @param  {Object} data
   *
   *         @param {String} data.query
   *                The SKU/code that was searched for
   *
   *         @param {Suggestion} data.pendingSuggestion
   *                The active suggestion in the list that would have been selected if feelingLucky had not been
   *                triggered
   *
   *         @param {Array<Suggestion>} data.suggestions
   *                The suggestions returned by the feeling lucky search
   *
   *         @param {Boolean} [data.isGlobal = false]
   *                Are we handling global "feeling lucky" suggestions
   *
   * @return {Suggestion|undefined} The suggestion to select or undefined if there is no suitable suggestion to select
   */
  private _handleFeelingLuckySuggestions({
    query,
    pendingSuggestion,
    suggestions,
    isGlobal = false,
  }: {
    query: string
    pendingSuggestion: $TSFixMe
    suggestions: $TSFixMe[]
    isGlobal?: boolean
  }) {
    let message
    if (suggestions.length > 1) {
      message = `More than 1 result found for "${query}". Try typing that into the search field to see results.`
    } else if (!suggestions.length) {
      message = `No results found for "${query}". Try searching by product name.`
    }

    if (message) {
      if (pendingSuggestion) {
        return pendingSuggestion
      }

      $log.debug(`[ProductSuggestionSource] ${message}`, suggestions)
      this.vdToastNotificationService.negative({ message })
      // Only emit the unknown product event if dealing with the main global search
      if (isGlobal) {
        // Prevent adding single character unknown product for "." & "+"
        if (query !== '.' && query !== '+') {
          $rootScope.$emit('search:unknown-product', {
            query,
            suggestions,
          })
        }
      }
      return
    }

    return suggestions[0]
  }
}
